import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import useAuthStore from '../../store/authStore';
import DoctorDropdown from '../../components/DoctorDropdown';
import ICD10Search from '../../components/ICD10Search';
import MedicineDropdown from '../../components/MedicineDropdown';
import AllergyAlert from '../../components/AllergyAlert';

const STATUS_COLORS = { Waiting: '#f59e0b', 'In Progress': '#2563eb', Completed: '#16a34a' };
const FREQ_OPTIONS = ['Once daily', 'Twice daily', 'Three times daily', 'Four times daily', 'Every 6 hours', 'Every 8 hours', 'Every 12 hours', 'Weekly', 'As needed (PRN)'];
const ROUTE_OPTIONS = ['Oral', 'IV', 'IM', 'SC', 'Topical', 'Inhaled', 'Sublingual', 'Rectal', 'Nasal'];
const DURATION_OPTIONS = ['1 day', '2 days', '3 days', '5 days', '7 days', '10 days', '14 days', '1 month', '2 months', '3 months', 'Ongoing'];

function VoiceButton({ onText, disabled }) {
  const [listening, setListening] = useState(false);
  const [lang, setLang] = useState('en-US');
  const [status, setStatus] = useState('');
  const recRef = useRef(null);
  const activeRef = useRef(false);

  const processText = async (text, langCode) => {
    if (!text.trim()) return;
    if (langCode === 'en-US') { onText(text.trim()); return; }
    setStatus('translating...');
    try {
      const srcLang = langCode.split('-')[0];
      const res = await fetch(`/api/translate?text=${encodeURIComponent(text.trim())}&from=${srcLang}`);
      const data = await res.json();
      onText(data.translated || text.trim());
    } catch {
      onText(text.trim());
    }
    setStatus('');
  };

  const startListening = (langCode) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Use Chrome or Edge for voice input.'); return; }

    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = langCode;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      processText(text, langCode);
    };

    rec.onerror = (e) => {
      if (e.error !== 'aborted') setStatus('');
    };

    rec.onend = () => {
      if (activeRef.current) {
        // auto-restart for continuous experience
        try { rec.start(); } catch { activeRef.current = false; setListening(false); setStatus(''); }
      } else {
        setListening(false);
        setStatus('');
      }
    };

    rec.start();
    recRef.current = rec;
  };

  const toggle = () => {
    if (listening) {
      activeRef.current = false;
      recRef.current?.abort();
      setListening(false);
      setStatus('');
    } else {
      activeRef.current = true;
      setListening(true);
      setStatus('listening...');
      startListening(lang);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <button type="button" onClick={toggle} disabled={disabled}
        style={{ padding: '4px 10px', border: `1px solid ${listening ? '#ef4444' : '#d1d5db'}`, borderRadius: 6, backgroundColor: listening ? '#fef2f2' : '#fff', cursor: 'pointer', fontSize: 12, color: listening ? '#ef4444' : '#6b7280', minWidth: 70 }}>
        {listening ? '⏹ Stop' : '🎤 Voice'}
      </button>
      {status && <span style={{ fontSize: 11, color: '#f59e0b' }}>{status}</span>}
      <select value={lang} onChange={e => setLang(e.target.value)} disabled={listening}
        style={{ padding: '3px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 11, color: '#374151' }}>
        <option value="en-US">English</option>
        <option value="ur-PK">Urdu</option>
        <option value="ar-SA">Arabic</option>
        <option value="hi-IN">Hindi</option>
        <option value="fa-IR">Farsi</option>
        <option value="ps-AF">Pashto</option>
      </select>
    </div>
  );
}

function SOAPSection({ label, value, onChange, fieldKey }) {
  const append = (text) => onChange(prev => ({ ...prev, [fieldKey]: (prev[fieldKey] ? prev[fieldKey] + ' ' : '') + text }));
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{label}</label>
        <VoiceButton onText={append} />
      </div>
      <textarea value={value} onChange={e => onChange(prev => ({ ...prev, [fieldKey]: e.target.value }))}
        rows={3} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
    </div>
  );
}

export default function OPDDoctorView() {
  const location = useLocation();
  const token = useAuthStore(s => s.token);
  const headers = { Authorization: `Bearer ${token}` };
  const params = new URLSearchParams(location.search);
  const preloadVisitId = params.get('visit_id');

  const [visits, setVisits] = useState([]);
  const [filterDoctor, setFilterDoctor] = useState('');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [msg, setMsg] = useState('');
  const [allergyDrug, setAllergyDrug] = useState(null);
  const [allergyIdx, setAllergyIdx] = useState(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [labTests, setLabTests] = useState([]);
  const [showLabModal, setShowLabModal] = useState(false);
  const [labSearch, setLabSearch] = useState('');
  const [labResults, setLabResults] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState([]);
  const [labPriority, setLabPriority] = useState('Routine');

  const [soap, setSoap] = useState({ soap_subjective: '', soap_objective: '', soap_assessment: '', soap_plan: '' });
  const [vitals, setVitals] = useState({ bp_systolic: '', bp_diastolic: '', pulse: '', temp: '', spo2: '', weight: '', height: '' });
  const [icdCodes, setIcdCodes] = useState([]);
  const [prescriptions, setPrescriptions] = useState([{ medicine: '', dose: '', frequency: 'Twice daily', duration: '7 days', route: 'Oral', instructions: '' }]);

  const bmi = vitals.weight && vitals.height
    ? (parseFloat(vitals.weight) / Math.pow(parseFloat(vitals.height) / 100, 2)).toFixed(1)
    : '';

  const fetchVisits = useCallback(async () => {
    try {
      const p = {};
      if (filterDoctor) p.doctor_id = filterDoctor;
      const res = await axios.get('/api/opd/today', { headers, params: p });
      setVisits(res.data || []);
    } catch { setVisits([]); }
  }, [filterDoctor]);

  useEffect(() => { fetchVisits(); const t = setInterval(fetchVisits, 30000); return () => clearInterval(t); }, [fetchVisits]);

  useEffect(() => {
    if (preloadVisitId && visits.length > 0) {
      const v = visits.find(x => x.id === parseInt(preloadVisitId));
      if (v) loadVisit(v);
    }
  }, [preloadVisitId, visits]);

  const loadVisit = async (visit) => {
    setSelectedVisit(visit);
    setSoap({ soap_subjective: visit.soap_subjective || '', soap_objective: visit.soap_objective || '', soap_assessment: visit.soap_assessment || '', soap_plan: visit.soap_plan || '' });
    setVitals(visit.vitals || { bp_systolic: '', bp_diastolic: '', pulse: '', temp: '', spo2: '', weight: '', height: '' });
    setIcdCodes(Array.isArray(visit.icd_codes) ? visit.icd_codes : []);
    setPrescriptions([{ medicine: '', dose: '', frequency: 'Twice daily', duration: '7 days', route: 'Oral', instructions: '' }]);
    setMsg('');
  };

  const handleSave = async (status) => {
    if (!selectedVisit) return;
    setSaving(true);
    try {
      await axios.put(`/api/opd/${selectedVisit.id}`, {
        ...soap, vitals, icd_codes: icdCodes,
        status: status || 'In Progress',
        prescriptions,
      }, { headers });
      fetchVisits();
      setMsg('Saved successfully.');
      if (status === 'Completed') { setShowFollowUp(true); setCompleting(false); }
    } catch { setMsg('Failed to save.'); }
    setSaving(false);
  };

  const handleComplete = () => { setCompleting(true); handleSave('Completed'); };

  const bookFollowUp = async () => {
    if (!followUpDate || !selectedVisit) return;
    try {
      await axios.post('/api/appointments', {
        patient_id: selectedVisit.patient_id,
        doctor_id: selectedVisit.doctor_id,
        doctor_name: selectedVisit.doctor,
        appointment_date: followUpDate,
        appointment_time: '09:00',
        reason: 'Follow-up',
        notes: `Follow-up after visit ${selectedVisit.visit_number}`,
      }, { headers });
      setShowFollowUp(false);
      setMsg('Follow-up appointment booked.');
    } catch { setMsg('Failed to book follow-up.'); }
  };

  const searchLabTests = async (q) => {
    setLabSearch(q);
    if (!q || q.length < 2) { setLabResults([]); return; }
    try {
      const res = await axios.get(`/api/lab/tests?q=${q}`, { headers });
      setLabResults(res.data || []);
    } catch { setLabResults([]); }
  };

  const submitLabOrder = async () => {
    if (!selectedLabs.length || !selectedVisit) return;
    try {
      await axios.post('/api/lab/orders', {
        patient_id: selectedVisit.patient_id,
        tests: selectedLabs.map(t => ({ test_id: t.id, test_name: t.name })),
        priority: labPriority,
        source: 'OPD',
        source_visit_id: selectedVisit.id,
        ordered_by: selectedVisit.doctor,
      }, { headers });
      setShowLabModal(false);
      setSelectedLabs([]);
      setMsg('Lab order submitted.');
    } catch { setMsg('Failed to submit lab order.'); }
  };

  const addPrescriptionRow = () => setPrescriptions(p => [...p, { medicine: '', dose: '', frequency: 'Twice daily', duration: '7 days', route: 'Oral', instructions: '' }]);
  const removePrescriptionRow = (i) => setPrescriptions(p => p.filter((_, idx) => idx !== i));
  const updatePrescription = (i, field, val) => setPrescriptions(p => p.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const handleMedicineSelect = (i, name, drug) => {
    const allergy = selectedVisit?.allergies;
    if (allergy && name) {
      const allergyList = allergy.split(/[,;/\n]/).map(a => a.trim().toLowerCase()).filter(Boolean);
      const nameLower = name.toLowerCase();
      const conflict = allergyList.find(a => nameLower.includes(a) || a.includes(nameLower.split(' ')[0]));
      if (conflict) { setAllergyDrug({ drug: name, conflict }); setAllergyIdx(i); return; }
    }
    updatePrescription(i, 'medicine', name);
  };

  const printConsultation = () => {
    if (!selectedVisit) return;
    const content = `<html><head><title>Consultation Note</title><style>body{font-family:Arial;padding:20px;font-size:13px}h2{margin:0 0 4px}h3{margin:12px 0 6px;font-size:14px;border-bottom:1px solid #ccc}table{width:100%;border-collapse:collapse}td,th{padding:4px 8px;border:1px solid #ddd;font-size:12px}th{background:#f5f5f5}</style></head><body>
      <h2>Consultation Note</h2><p>${new Date().toLocaleString('en-PK')}</p>
      <table style="margin-bottom:12px"><tr><td>Patient:</td><td>${selectedVisit.patient_name} (${selectedVisit.mrn})</td><td>Doctor:</td><td>${selectedVisit.doctor || '-'}</td></tr></table>
      ${soap.soap_subjective ? `<h3>Subjective</h3><p>${soap.soap_subjective}</p>` : ''}
      ${soap.soap_objective ? `<h3>Objective</h3><p>${soap.soap_objective}</p>` : ''}
      ${soap.soap_assessment ? `<h3>Assessment</h3><p>${soap.soap_assessment}</p>` : ''}
      ${soap.soap_plan ? `<h3>Plan</h3><p>${soap.soap_plan}</p>` : ''}
      ${icdCodes.length ? `<h3>Diagnosis</h3>${icdCodes.map(d => `<div>${d.type === 'Primary' ? '1°' : '2°'} ${d.code} — ${d.description}</div>`).join('')}` : ''}
      </body></html>`;
    const w = window.open('', '_blank', 'width=600,height=700');
    if (w) { w.document.open(); w.document.write(content); w.document.close(); w.print(); }
  };

  const printPrescription = () => {
    if (!selectedVisit) return;
    const meds = prescriptions.filter(p => p.medicine);
    if (!meds.length) { alert('No medicines prescribed.'); return; }
    const content = `<html><head><title>Prescription</title><style>body{font-family:Arial;padding:20px;font-size:13px}h2{margin:0 0 4px}.rx{font-size:28px;font-weight:bold}table{width:100%;border-collapse:collapse}td,th{padding:6px 8px;border:1px solid #ddd;font-size:12px}th{background:#f5f5f5}</style></head><body>
      <div class="rx">Rx</div>
      <p><strong>Patient:</strong> ${selectedVisit.patient_name} (${selectedVisit.mrn}) &nbsp;&nbsp; <strong>Date:</strong> ${new Date().toLocaleDateString('en-PK')}</p>
      <p><strong>Doctor:</strong> ${selectedVisit.doctor || '-'}</p><hr/>
      <table><thead><tr><th>#</th><th>Medicine</th><th>Dose</th><th>Frequency</th><th>Duration</th><th>Route</th><th>Instructions</th></tr></thead><tbody>
        ${meds.map((m, i) => `<tr><td>${i + 1}</td><td>${m.medicine}</td><td>${m.dose || '-'}</td><td>${m.frequency}</td><td>${m.duration}</td><td>${m.route}</td><td>${m.instructions || '-'}</td></tr>`).join('')}
      </tbody></table>
      <br/><br/><div style="text-align:right">___________________________<br/>Doctor's Signature & Stamp</div>
      </body></html>`;
    const w = window.open('', '_blank', 'width=700,height=600');
    if (w) { w.document.open(); w.document.write(content); w.document.close(); w.print(); }
  };

  const sendToPharmacy = async () => {
    const meds = prescriptions.filter(p => p.medicine);
    if (!meds.length || !selectedVisit) return;
    try {
      await axios.post('/api/pharmacy/opd-request', {
        patient_id: selectedVisit.patient_id,
        visit_id: selectedVisit.id,
        medicines: meds,
        doctor: selectedVisit.doctor,
      }, { headers });
      setMsg('Sent to pharmacy.');
    } catch { setMsg('Failed to send to pharmacy.'); }
  };

  const admitToIPD = async () => {
    if (!selectedVisit) return;
    const diag = icdCodes[0]?.description || soap.soap_assessment || 'OPD Referral';
    window.location.href = `/ipd/register?patient_id=${selectedVisit.patient_id}&diagnosis=${encodeURIComponent(diag)}&from_opd=${selectedVisit.id}`;
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      {/* Left panel — queue */}
      <div style={{ width: 320, borderRight: '1px solid #e5e7eb', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Today's Queue</div>
          <DoctorDropdown value={filterDoctor} onChange={(id) => setFilterDoctor(id)} label="" placeholder="Filter by doctor" />
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {visits.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No visits today</div>
          ) : visits.map(v => (
            <div key={v.id} onClick={() => loadVisit(v)}
              style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                backgroundColor: selectedVisit?.id === v.id ? '#eff6ff' : '#fff',
                borderLeft: selectedVisit?.id === v.id ? '3px solid #2563eb' : '3px solid transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{v.patient_name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{v.mrn} | {v.age}y {v.gender}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{v.chief_complaint || 'No complaint'}</div>
                  {v.doctor && <div style={{ fontSize: 11, color: '#9ca3af' }}>Dr. {v.doctor}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLORS[v.status] || '#6b7280' }}>{v.visit_number || `#${v.id}`}</div>
                  <div style={{ fontSize: 11, padding: '2px 6px', borderRadius: 10, backgroundColor: '#f3f4f6', color: '#6b7280', marginTop: 4 }}>{v.status}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — consultation */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {!selectedVisit ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 48 }}>🏥</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Select a patient from the queue</div>
          </div>
        ) : (
          <>
            {/* Patient header */}
            <div style={{ padding: '16px 24px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 20, fontWeight: 700 }}>{selectedVisit.patient_name}</span>
                    <span style={{ fontSize: 13, color: '#9ca3af' }}>MRN: {selectedVisit.mrn}</span>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>{selectedVisit.age}y {selectedVisit.gender}</span>
                    {selectedVisit.blood_group && <span style={{ fontSize: 12, padding: '2px 8px', backgroundColor: '#fef3c7', borderRadius: 10 }}>{selectedVisit.blood_group}</span>}
                  </div>
                  {selectedVisit.allergies && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 6 }}>
                      {selectedVisit.allergies.split(/[,;]/).map((a, i) => a.trim() && (
                        <span key={i} style={{ fontSize: 11, padding: '2px 8px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10 }}>⚠ {a.trim()}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleSave()} disabled={saving} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Save</button>
                  <button onClick={handleComplete} disabled={saving} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, backgroundColor: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Complete</button>
                  <button onClick={printConsultation} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Print Note</button>
                  <button onClick={printPrescription} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Print Rx</button>
                  <button onClick={admitToIPD} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, backgroundColor: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 13 }}>Admit IPD</button>
                </div>
              </div>
              {msg && <div style={{ marginTop: 8, fontSize: 13, color: msg.includes('Failed') ? '#ef4444' : '#16a34a' }}>{msg}</div>}
            </div>

            <div style={{ padding: 24 }}>
              {/* Vitals */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Vitals</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  {[['BP Systolic', 'bp_systolic', 'mmHg'], ['BP Diastolic', 'bp_diastolic', 'mmHg'], ['Pulse', 'pulse', '/min'], ['Temperature', 'temp', '°F'], ['SpO2', 'spo2', '%'], ['Weight', 'weight', 'kg'], ['Height', 'height', 'cm']].map(([label, key, unit]) => (
                    <div key={key}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 3 }}>{label} ({unit})</label>
                      <input type="number" value={vitals[key] || ''} onChange={e => setVitals(v => ({ ...v, [key]: e.target.value }))}
                        style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                  ))}
                  {bmi && (
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 3 }}>BMI</label>
                      <div style={{ padding: '7px 8px', backgroundColor: '#f9fafb', borderRadius: 6, fontSize: 14, fontWeight: 700, color: parseFloat(bmi) > 30 ? '#dc2626' : parseFloat(bmi) < 18.5 ? '#f59e0b' : '#16a34a' }}>{bmi}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* SOAP Notes */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>SOAP Notes</div>
                <SOAPSection label="S — Subjective (Patient's complaints)" value={soap.soap_subjective} onChange={setSoap} fieldKey="soap_subjective" />
                <SOAPSection label="O — Objective (Examination findings)" value={soap.soap_objective} onChange={setSoap} fieldKey="soap_objective" />
                <SOAPSection label="A — Assessment (Diagnosis / Clinical impression)" value={soap.soap_assessment} onChange={setSoap} fieldKey="soap_assessment" />
                <SOAPSection label="P — Plan (Treatment & instructions)" value={soap.soap_plan} onChange={setSoap} fieldKey="soap_plan" />
              </div>

              {/* Diagnosis */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Diagnosis (ICD-10)</div>
                <ICD10Search value={icdCodes} onChange={setIcdCodes} label="" />
              </div>

              {/* Lab Orders */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>Lab Orders</div>
                  <button onClick={() => setShowLabModal(true)} style={{ padding: '6px 14px', border: 'none', borderRadius: 6, backgroundColor: '#0891b2', color: '#fff', cursor: 'pointer', fontSize: 13 }}>+ Order Lab</button>
                </div>
                <div style={{ fontSize: 13, color: '#9ca3af' }}>Lab orders placed from this visit will appear in the Lab Worklist.</div>
              </div>

              {/* Prescription */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>Prescription</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={sendToPharmacy} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Send to Pharmacy</button>
                    <button onClick={addPrescriptionRow} style={{ padding: '6px 14px', border: 'none', borderRadius: 6, backgroundColor: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13 }}>+ Add Row</button>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        {['Medicine', 'Dose', 'Frequency', 'Duration', 'Route', 'Instructions', ''].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {prescriptions.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 8px', minWidth: 200 }}>
                            <MedicineDropdown value={row.medicine} onChange={(name, drug) => handleMedicineSelect(i, name, drug)} placeholder="Search medicine..." showStock />
                          </td>
                          <td style={{ padding: '6px 8px', minWidth: 80 }}>
                            <input type="text" value={row.dose} onChange={e => updatePrescription(i, 'dose', e.target.value)}
                              placeholder="e.g. 500mg" style={{ width: '100%', padding: '5px 8px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 13 }} />
                          </td>
                          <td style={{ padding: '6px 8px', minWidth: 130 }}>
                            <select value={row.frequency} onChange={e => updatePrescription(i, 'frequency', e.target.value)}
                              style={{ width: '100%', padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 12 }}>
                              {FREQ_OPTIONS.map(f => <option key={f}>{f}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px', minWidth: 100 }}>
                            <select value={row.duration} onChange={e => updatePrescription(i, 'duration', e.target.value)}
                              style={{ width: '100%', padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 12 }}>
                              {DURATION_OPTIONS.map(d => <option key={d}>{d}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px', minWidth: 90 }}>
                            <select value={row.route} onChange={e => updatePrescription(i, 'route', e.target.value)}
                              style={{ width: '100%', padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 12 }}>
                              {ROUTE_OPTIONS.map(r => <option key={r}>{r}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px', minWidth: 120 }}>
                            <input type="text" value={row.instructions} onChange={e => updatePrescription(i, 'instructions', e.target.value)}
                              placeholder="e.g. After meals" style={{ width: '100%', padding: '5px 8px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 13 }} />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {prescriptions.length > 1 && (
                              <button onClick={() => removePrescriptionRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16 }}>×</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Follow-up */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Follow-up</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)}
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  {[3, 7, 14, 30].map(d => (
                    <button key={d} type="button" onClick={() => {
                      const dt = new Date(); dt.setDate(dt.getDate() + d);
                      setFollowUpDate(dt.toISOString().slice(0, 10));
                    }} style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: 12 }}>
                      +{d}d
                    </button>
                  ))}
                  <button onClick={bookFollowUp} disabled={!followUpDate} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, backgroundColor: followUpDate ? '#2563eb' : '#93c5fd', color: '#fff', cursor: followUpDate ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                    Book Follow-up
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Allergy Alert */}
      {allergyDrug && (
        <AllergyAlert
          patientAllergies={selectedVisit?.allergies}
          selectedDrug={allergyDrug.drug}
          onConfirm={(reason) => {
            updatePrescription(allergyIdx, 'medicine', allergyDrug.drug);
            setAllergyDrug(null); setAllergyIdx(null);
          }}
          onCancel={() => { setAllergyDrug(null); setAllergyIdx(null); }}
        />
      )}

      {/* Lab Order Modal */}
      {showLabModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 28, width: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Order Lab Tests</h3>
              <button onClick={() => setShowLabModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <input type="text" value={labSearch} onChange={e => searchLabTests(e.target.value)}
              placeholder="Search test name..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />
            {labResults.map(t => (
              <div key={t.id} onClick={() => !selectedLabs.find(s => s.id === t.id) && setSelectedLabs(l => [...l, t])}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', backgroundColor: selectedLabs.find(s => s.id === t.id) ? '#eff6ff' : '#fff' }}>
                <span style={{ fontSize: 13 }}>{t.name}</span>
                {t.code && <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>{t.code}</span>}
              </div>
            ))}
            {selectedLabs.length > 0 && (
              <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Selected: {selectedLabs.map(t => t.name).join(', ')}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 13 }}>Priority:</span>
                  {['Routine', 'Urgent', 'STAT'].map(p => (
                    <button key={p} onClick={() => setLabPriority(p)} style={{ padding: '4px 10px', border: `1px solid ${labPriority === p ? '#2563eb' : '#d1d5db'}`, borderRadius: 4, backgroundColor: labPriority === p ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: 12 }}>{p}</button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowLabModal(false)} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitLabOrder} disabled={!selectedLabs.length} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, backgroundColor: selectedLabs.length ? '#0891b2' : '#a5f3fc', color: '#fff', cursor: selectedLabs.length ? 'pointer' : 'not-allowed', fontWeight: 600 }}>Submit Order</button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up prompt after completing */}
      {showFollowUp && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 28, width: 420 }}>
            <h3 style={{ margin: '0 0 12px' }}>Schedule Follow-up?</h3>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 16px' }}>Consultation marked as completed. Would you like to book a follow-up appointment?</p>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                {[7, 14, 30].map(d => (
                  <button key={d} onClick={() => { const dt = new Date(); dt.setDate(dt.getDate() + d); setFollowUpDate(dt.toISOString().slice(0, 10)); }} style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>+{d} days</button>
                ))}
              </div>
              <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowFollowUp(false)} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer' }}>Skip</button>
              <button onClick={bookFollowUp} disabled={!followUpDate} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, backgroundColor: followUpDate ? '#2563eb' : '#93c5fd', color: '#fff', cursor: followUpDate ? 'pointer' : 'not-allowed', fontWeight: 600 }}>Book Follow-up</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
